import { getValidToken } from '../utils/tokenUtils';
import { fetchWithRateLimitRetry } from '../utils/fetchWithRateLimitRetry';


export const fetchAllTestExecutions = async () => {
  const token = await getValidToken();
  
  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }
  
  const res = await fetch(`/api/test-executions/executions/in-review`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte hämta testkörningar (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
};

// Fetches the current user's own test executions for an organisation, optionally filtered by contract.
// When sandbox=true, fetches ONLY the isolated sandbox executions instead — the two sets are never mixed.
export const fetchTestExecutions = async (organisationId, contractId, sandbox = false) => {
    if (!organisationId) {
        throw new Error('Organisationsinformation saknas. Välj organisation och försök igen.');
    }
    
    const token = await getValidToken();
    
    if (!token) {
        throw new Error('Din session har gått ut. Logga in igen.');
    }

    const params = new URLSearchParams({ organisationId });
    if (contractId != null) {
        params.append('contractId', contractId);
    }
    if (sandbox) {
        params.append('sandbox', 'true');
    }

    const res = await fetch(`/api/test-executions/executions?${params.toString()}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Kunde inte hämta testkörningar (${res.status}).` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
};

// Fetches every submitted test execution in the organisation, no matter who created it.
// Drafts stay private to their author, so they are never included here.
export const fetchOrganisationTestExecutions = async (organisationId, contractId) => {
    if (!organisationId) {
        throw new Error('Organisationsinformation saknas. Välj organisation och försök igen.');
    }

    const token = await getValidToken();

    if (!token) {
        throw new Error('Din session har gått ut. Logga in igen.');
    }

    const params = new URLSearchParams({ organisationId });
    if (contractId != null) {
        params.append('contractId', contractId);
    }

    const res = await fetch(`/api/test-executions/executions/organisation?${params.toString()}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Kunde inte hämta testkörningar (${res.status}).` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
};

// Fetches metadata-only summaries for the report list. The list shows organisation, contract,
// status, submitter, reviewer and test counts only, so the full executions (every test result with
// payloads, logs and comments) are deliberately not fetched here.
export const fetchTestExecutionSummaries = async (organisationId, contractId) => {
    const token = await getValidToken();

    if (!token) {
        throw new Error('Din session har gått ut. Logga in igen.');
    }

    const params = new URLSearchParams();
    if (organisationId) {
        params.append('organisationId', organisationId);
    }
    if (contractId != null) {
        params.append('contractId', contractId);
    }

    const res = await fetch(`/api/test-executions/executions/summary?${params.toString()}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Kunde inte hämta testkörningar (${res.status}).` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
};

// Metadata-only counterpart of fetchOrganisationTestExecutions, used by the tester report list.
export const fetchOrganisationTestExecutionSummaries = async (organisationId, contractId) => {
    if (!organisationId) {
        throw new Error('Organisationsinformation saknas. Välj organisation och försök igen.');
    }

    const token = await getValidToken();

    if (!token) {
        throw new Error('Din session har gått ut. Logga in igen.');
    }

    const params = new URLSearchParams({ organisationId });
    if (contractId != null) {
        params.append('contractId', contractId);
    }

    const res = await fetch(`/api/test-executions/executions/organisation/summary?${params.toString()}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Kunde inte hämta testkörningar (${res.status}).` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
};

/**
 * Creates a self-declaration ("manual" / scenario 3) execution that has no automated
 * test run behind it, so the questionnaire-only flow has a report to submit.
 * Returns the created TestExecution (RUNNING); the caller then sets it to PENDING_REVIEW.
 */
export const createManualDeclarationExecution = async (contractId, organisationId) => {
  if (!contractId && contractId !== 0) {
    throw new Error('Kontrakts-ID saknas. Välj ett kontrakt och försök igen.');
  }

  const token = await getValidToken();

  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }

  const params = new URLSearchParams({ contractId });
  if (organisationId) {
    params.append('organisationId', organisationId);
  }

  const res = await fetchWithRateLimitRetry(`/api/test-executions/manual-declaration?${params.toString()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to create declaration execution: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
};

/** Fetch all self-declaration question comments for a test execution (review). */
export const fetchQuestionComments = async (executionId) => {
  const token = await getValidToken();
  if (!token) throw new Error('Din session har gått ut. Logga in igen.');
  const res = await fetch(`/api/test-executions/execution/${encodeURIComponent(executionId)}/question-comments`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte hämta frågekommentarer (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/**
 * Fetch a single test execution the current user is allowed to open.
 *
 * Used when a review page is opened by URL or reloaded. Scanning an organisation-scoped list
 * would not work for a reviewer, whose own organisation never holds the submissions they review.
 * Returns null when the execution does not exist or is not accessible.
 */
export const fetchTestExecutionById = async (executionId) => {
  const token = await getValidToken();
  if (!token) throw new Error('Din session har gått ut. Logga in igen.');
  const res = await fetch(`/api/test-executions/execution/${encodeURIComponent(executionId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte hämta testrapporten (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/** Add a comment to a question within a test execution (review). */
export const saveQuestionComment = async (executionId, questionId, commentText) => {
  const token = await getValidToken();
  if (!token) throw new Error('Din session har gått ut. Logga in igen.');
  const res = await fetch(
    `/api/test-executions/execution/${encodeURIComponent(executionId)}/questions/${encodeURIComponent(questionId)}/comments`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ commentText }),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte spara frågekommentar (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/** Delete a question comment (backend allows this only for the comment's author). */
export const deleteQuestionComment = async (commentId) => {
  const token = await getValidToken();
  if (!token) throw new Error('Din session har gått ut. Logga in igen.');
  const res = await fetch(`/api/test-executions/question-comments/${encodeURIComponent(commentId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to delete question comment: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }
};

/** Edit a question comment's text (backend allows this only for the comment's author). */
export const updateQuestionComment = async (commentId, commentText) => {
  const token = await getValidToken();
  if (!token) throw new Error('Din session har gått ut. Logga in igen.');
  const res = await fetch(`/api/test-executions/question-comments/${encodeURIComponent(commentId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ commentText }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte uppdatera frågekommentar (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/**
 * @param decisionComment the reviewer's closing remark; only recorded when the status being
 *                        set is the final APPROVED or REJECTED, ignored otherwise
 */
export const setTestExecutionStatus = async (testExecutionId, testExecutionStatus, decisionComment) => {
  // Validate input early to avoid sending "undefined" to the backend
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }
  if (!testExecutionStatus || typeof testExecutionStatus !== 'string') {
    throw new Error('Ogiltig status. Kontakta support.');
  }

  const token = await getValidToken();

  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }

  const res = await fetchWithRateLimitRetry(`/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/status?status=${encodeURIComponent(testExecutionStatus)}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ decisionComment: decisionComment ?? null }),
    } );

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Failed to set test execution status: ${res.status}` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
}

/**
 * Builds the decision document for a review that was decided but has none — a decision made
 * before decision documents existed, or one whose document failed to render. Reviewer-only.
 *
 * @returns the updated execution, whose hasDecisionDocument is then true
 */
export const generateDecisionDocument = async (testExecutionId) => {
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }

  const token = await getValidToken();
  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }

  const res = await fetch(
    `/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/decision-document`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!res.ok) {
    const err = new Error(`Failed to generate decision document: ${res.status}`);
    err.status = res.status;
    throw err;
  }

  return res.json();
};

/**
 * Attaches the decision PDF to the ServiceNow case the review's service order belongs to.
 * Normally done by the backend as soon as the document is rendered; this is the reviewer's
 * retry for when that failed. Safe to repeat — an already attached document is not sent twice.
 *
 * @returns the updated execution, carrying decisionDocumentCaseNumber and ...UploadedAt
 */
export const uploadDecisionDocumentToServiceNow = async (testExecutionId) => {
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }

  const token = await getValidToken();
  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }

  const res = await fetch(
    `/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/decision-document/servicenow`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!res.ok) {
    const err = new Error(`Failed to upload decision document to ServiceNow: ${res.status}`);
    err.status = res.status;
    throw err;
  }

  return res.json();
};

/**
 * Downloads the archived decision PDF for a finished review and hands it to the browser.
 * The document is rendered server-side when the decision is made, so this only fetches it.
 */
export const downloadDecisionDocument = async (testExecutionId) => {
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }

  const token = await getValidToken();
  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }

  const res = await fetch(
    `/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/decision-document`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (res.status === 404) {
    throw new Error('Beslutsunderlaget saknas för den här granskningen.');
  }
  if (!res.ok) {
    const err = new Error(`Failed to download decision document: ${res.status}`);
    err.status = res.status;
    throw err;
  }

  // The filename lives in Content-Disposition; fall back to a sensible one if it is absent.
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const fileName = match ? decodeURIComponent(match[1]) : `granskningsbeslut-${testExecutionId}.pdf`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const setReviewedByUserId = async (testExecutionId) => {
  // Validate input early to avoid sending "undefined" to the backend
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }

  const token = await getValidToken();
  
  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }
  
  const res = await fetch(`/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/reviewed-by-user-id`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to set reviewed by user ID: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
}

export const createOrUpdateDeviation = async (testExecutionId, deviationData) => {
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }

  const token = await getValidToken();
  
  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }
  
  const res = await fetch(`/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/deviation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(deviationData),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to create/update deviation: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
}

export const getDeviation = async (testExecutionId) => {
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }

  const token = await getValidToken();
  
  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }
  
  const res = await fetch(`/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/deviation`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.status === 404) {
    return null; // No deviation exists
  }

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to get deviation: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
}

export const previewDeviationHandling = async (
  testExecutionId,
  testResultIds,
  generalExceptionKeys = {},
  similarTicketKeys = {},
  overrideTkbRules = undefined,
  sharedBodyNote = '',
  perTicketBodyNotes = {}
) => {
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('testExecutionId is required');
  }
  if (!Array.isArray(testResultIds) || testResultIds.length === 0) {
    throw new Error('testResultIds must be a non-empty array');
  }

  const token = await getValidToken();

  if (!token) {
    throw new Error('No valid authentication token available');
  }

  const res = await fetch(`/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/preview-deviation-handling`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      testResultIds,
      generalExceptionKeys,
      similarTicketKeys,
      overrideTkbRules,
      sharedBodyNote,
      perTicketBodyNotes,
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to preview deviation handling: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
}

export const startDeviationHandling = async (
  testExecutionId,
  testResultIds,
  generalExceptionKeys = {},
  similarTicketKeys = {},
  overrideTkbRules = undefined,
  sharedBodyNote = '',
  perTicketBodyNotes = {}
) => {
  if (!testExecutionId && testExecutionId !== 0) {
    throw new Error('Rapport-ID saknas. Försök igen.');
  }
  if (!Array.isArray(testResultIds) || testResultIds.length === 0) {
    throw new Error('Inga testresultat valda. Välj minst ett testresultat.');
  }

  const token = await getValidToken();

  if (!token) {
    throw new Error('Din session har gått ut. Logga in igen.');
  }

  const res = await fetch(`/api/test-executions/execution/${encodeURIComponent(testExecutionId)}/start-deviation-handling`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      testResultIds,
      generalExceptionKeys,
      similarTicketKeys,
      overrideTkbRules,
      sharedBodyNote,
      perTicketBodyNotes,
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to start deviation handling: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }
}
